import { useCallback, useEffect } from 'react'
import { useReactiveVar, useMutation, useLazyQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import { useArtistInfo } from 'modules/Projects/serviceHooks'

import { useLoading } from 'shared/hooks'
import { TRouterParams } from 'shared/types'
import { countryCodeVar } from 'modulesCommon/store'
import {
  GET_PRODUCTS_BY_CAMPAIGN,
  CREATE_PRODUCT,
  CREATE_LOCAL_PRODUCT,
  REMOVE_LOCAL_PRODUCT,
  REMOVE_ALTER_NAME,
  CREATE_ALTER_NAME
} from '../constants'
import { IProductsByCampaign, ICreateProductArguments } from '../types'
import { useProductUpdate } from './useProductUpdate'
import { IAlterName } from 'shared/interfaces'
import { FetchResult } from '@apollo/client/link/core'

export const useProducts = (onComplete: (data: ReadonlyArray<IProductsByCampaign>) => void) => {
  const { id: project_id } = useParams<TRouterParams>()
  const countryCode = useReactiveVar(countryCodeVar)
  const { activeProject, activeArtist } = useArtistInfo(project_id)

  const [getProduct, { loading: productsLoading }] = useLazyQuery<{
    getProductsByCampaignId: ReadonlyArray<IProductsByCampaign>
  }>(GET_PRODUCTS_BY_CAMPAIGN, {
    variables: { campaignId: project_id, countryCode },
    onCompleted(data) {
      onComplete(data.getProductsByCampaignId)
    }
  })

  const [createProducts, { loading: createProductsLoading }] =
    useMutation<{ createProducts: ReadonlyArray<IProductsByCampaign> }>(CREATE_PRODUCT)

  const [createAlterNameProducts, { loading: createAlterNameLoading }] = useMutation<{
    createAlterName: Readonly<IAlterName>
  }>(CREATE_ALTER_NAME, {
    update(cache, { data }) {
      if (typeof data?.createAlterName !== 'undefined') {
        const res = cache.readQuery<{ getProductsByCampaignId: IProductsByCampaign[] }>({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode }
        })

        cache.writeQuery({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode },
          data: {
            getProductsByCampaignId: (res?.getProductsByCampaignId ?? []).map((product) =>
              product.parent_id === `${data?.createAlterName.product_id}`
                ? {
                  ...product,
                  alter_names: [...(product?.alter_names ?? []), data?.createAlterName]
                }
                : product
            )
          }
        })
      }
    }
  })

  const [removeAlterNameProducts, { loading: removeAlterNameLoading }] = useMutation<{
    removeAlterName: Readonly<IAlterName>
  }>(REMOVE_ALTER_NAME, {
    update(cache, { data }) {
      if (typeof data?.removeAlterName !== 'undefined') {
        const res = cache.readQuery<{ getProductsByCampaignId: IProductsByCampaign[] }>({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode }
        })

        cache.writeQuery({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode },
          data: {
            getProductsByCampaignId: (res?.getProductsByCampaignId ?? []).map((product) =>
              product.parent_id === `${data?.removeAlterName.product_id}`
                ? {
                  ...product,
                  alter_names: product?.alter_names?.filter((f) => f.id !== data?.removeAlterName.id)
                }
                : product
            )
          }
        })
      }
    }
  })

  const [createLocalProductItem, { loading: createLocalProductsLoading }] = useMutation<{
    createLocalProduct: IProductsByCampaign
  }>(CREATE_LOCAL_PRODUCT, {
    update(cache, { data }) {
      if (typeof data?.createLocalProduct !== 'undefined') {
        const res = cache.readQuery<{ getProductsByCampaignId: IProductsByCampaign[] }>({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode }
        })

        const localProducts = [...(res?.getProductsByCampaignId ?? []), data?.createLocalProduct]

        cache.writeQuery({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode },
          data: {
            getProductsByCampaignId: localProducts
          }
        })

        onComplete(localProducts)
      }
    }
  })

  const [removeLocalProductItem, { loading: removeProductsLoading }] = useMutation<{
    updateProduct: IProductsByCampaign
  }>(REMOVE_LOCAL_PRODUCT, {
    update(cache, { data }, { variables }) {
      if (typeof variables !== 'undefined') {
        const res = cache.readQuery<{ getProductsByCampaignId: IProductsByCampaign[] }>({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode }
        })

        cache.writeQuery({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode },
          data: {
            getProductsByCampaignId: (res?.getProductsByCampaignId ?? []).filter((f) => f.id !== variables.id)
          }
        })
      }
    }
  })

  const { updateProduct } = useProductUpdate()

  const updateProductItem = useCallback<(data: IProductsByCampaign) => void>(
    (data) => {
      updateProduct(data)
    },
  [updateProduct]
  )

  const createProductItem = useCallback<(data: ICreateProductArguments) => void>(
    async ({ configuration, name, publish_date, gpid_list, gps_status }) => {
      const { data: dataProduct } = await createProducts({
        variables: {
          input: [
            {
              artist_id: activeArtist.id,
              configuration,
              gps_status,
              name,
              ...(gpid_list && { gpid_list })
            }
          ]
        }
      })
      if (Array.isArray(dataProduct?.createProducts) && dataProduct?.createProducts.length === 1) {
        const item = dataProduct.createProducts[0]

        await createLocalProductItem({
          variables: {
            input: {
              active: true,
              campaign_id: project_id,
              label_id: activeProject.label_id,
              main: false,
              product_id: item.id,
              publish_date
            }
          }
        })
      }
      // TODO: Add error handlers
    },
  [createProducts, activeArtist.id, createLocalProductItem, project_id, activeProject.label_id]
  )

  const removeProduct = useCallback<(id: string) => void>(
    async (id) => {
      await removeLocalProductItem({ variables: { id } })
    },
  [removeLocalProductItem]
  )

  const createAlterName = useCallback(
    async (input: {
      artist_name: string;
      product_name: string;
      product_id: string
    }): Promise<FetchResult<{ createAlterName: Readonly<IAlterName> }>> =>
      await createAlterNameProducts({ variables: { input } }),
    [createAlterNameProducts]
  )

  const removeAlterName = useCallback(
    async (id: string) => {
      await removeAlterNameProducts({ variables: { id } })
    },
    [removeAlterNameProducts]
  )

  useEffect(() => {
    if (countryCode && countryCode.length > 0) {
      getProduct()
    }
  }, [getProduct, countryCode])

  useLoading({
    isLoading:
      productsLoading ||
      createProductsLoading ||
      createLocalProductsLoading ||
      removeProductsLoading ||
      createAlterNameLoading ||
      removeAlterNameLoading
  })

  return {
    removeProduct,
    createProductItem,
    updateProduct: updateProductItem,
    createAlterName,
    removeAlterName,
  }
}
